import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, RefreshCw } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Spinner } from '../../ui/Spinner/Spinner'
import { Switch } from '../../ui/Switch/Switch'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import { ApiError } from '../../services/api/client'
import type { AssistantBehavior } from './assistant-settings-contracts'
import type { AssistantSettingsRepository } from './assistant-settings-repository'
import { CallHandlingSettings } from './CallHandlingSettings'
import { RulesPanel } from './RulesPanel'
import { InsuranceLists } from './InsuranceLists'
import {
  insuranceFromBehavior,
  insurancePayload,
  insuranceProblem,
  type InsuranceDraft,
} from './insurance-model'
import { VoiceChoiceGroup, type VoiceChoice } from './VoiceChoiceGroup'
import { SettingsSaveFooter } from './SettingsSaveFooter'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import panel from './SettingsSection.module.css'
import styles from './AiRulesSettings.module.css'

const PLATFORM_ASSISTANT_NAME = 'Ava'
type SpeakerKey = NonNullable<AssistantBehavior['voiceProfile']>['speakerKey']
type SpeakingPace = NonNullable<
  NonNullable<AssistantBehavior['voiceProfile']>['speakingPace']
>
type BackgroundAmbience = NonNullable<
  NonNullable<AssistantBehavior['voiceProfile']>['backgroundAmbience']
>
const DEFAULT_SPEAKER_KEY: SpeakerKey = 'jessica'
const DEFAULT_SPEAKING_PACE: SpeakingPace = 'RELAXED'
const DEFAULT_BACKGROUND_AMBIENCE: BackgroundAmbience = 'OFFICE'
/** Voices offered for Ava, who is a woman. */
const CURATED_SPEAKERS: Array<VoiceChoice<SpeakerKey>> = [
  { key: 'jessica', name: 'Jessica', description: 'Playful · Bright · Warm' },
  { key: 'bella', name: 'Bella', description: 'Professional · Bright · Warm' },
  {
    key: 'sarah',
    name: 'Sarah',
    description: 'Mature · Reassuring · Confident',
  },
]
/**
 * No longer offered, but still valid on the server: a clinic that already
 * uses it sees it (and keeps it) until staff choose another voice.
 */
const RETIRED_SPEAKER: VoiceChoice<SpeakerKey> = {
  key: 'eric',
  name: 'Eric',
  description: 'Male voice · no longer offered',
}
const CURATED_SPEAKING_PACES: Array<VoiceChoice<SpeakingPace>> = [
  { key: 'RELAXED', name: 'Relaxed', description: 'Natural and unhurried.' },
  {
    key: 'BALANCED',
    name: 'Balanced',
    description: 'A little quicker while staying conversational.',
  },
  { key: 'BRISK', name: 'Brisk', description: 'Faster and efficient.' },
]
const CURATED_BACKGROUND_AMBIENCES: Array<VoiceChoice<BackgroundAmbience>> = [
  {
    key: 'OFFICE',
    name: 'Office',
    description: 'Subtle office sounds behind Ava.',
  },
  { key: 'QUIET', name: 'Quiet', description: 'No background sound.' },
]

const EMPTY_BEHAVIOR: AssistantBehavior = {
  aiName: PLATFORM_ASSISTANT_NAME,
  firstMessage: '',
  language: 'English',
  approvedInstructions: [],
  insuranceInformation: [],
  acceptedInsurances: null,
  escalationMode: 'NONE',
  keypadLanguageSwitchEnabled: false,
  maxDurationSeconds: 600,
  backgroundSound: 'office',
  endCallMessage: '',
}
const EMPTY_INSURANCE: InsuranceDraft = {
  proactive: true,
  carriers: [],
  notes: [],
}
const GREETING_MAX = 500
const TEXT_TOKENS = ['{assistantName}', '{clinicName}'] as const

function isPhoneSafeGreeting(value: string) {
  return !/\b(?:this is|you(?:'ve| have) reached)\b[\s\S]{0,40}\btext (?:line|message)\b/i.test(
    value
  )
}

function outdated(error: unknown) {
  return (
    error instanceof ApiError && error.reason === 'dashboard_client_outdated'
  )
}

/** The owner saved the behavior but couldn't refresh the live assistant. */
function committedBeforeVoiceSync(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.reason === 'communications_provider_rejected' ||
      error.reason === 'communications_provider_unavailable')
  )
}

type Outcome = { kind: 'saved' } | { kind: 'error'; message: string } | null

export function AssistantSettings({
  tenantId,
  tenantLabel,
  repository,
}: {
  tenantId: string
  tenantLabel: string
  repository: AssistantSettingsRepository
}) {
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  const behaviorKey = ['settings', tenantId, 'assistant-behavior'] as const
  const behaviorQuery = useQuery({
    queryKey: ['settings', tenantId, 'assistant-behavior'],
    queryFn: () => repository.getBehavior(tenantId),
  })
  const textGreetingQuery = useQuery({
    queryKey: ['settings', tenantId, 'text-greeting'],
    queryFn: () => repository.getTextGreeting(tenantId),
  })
  const rulesQuery = useQuery({
    queryKey: ['settings', tenantId, 'special-rules'],
    queryFn: () => repository.getRules(tenantId),
  })
  // The saved behavior as last read; drafts live in their own state.
  const [behavior, setBehavior] = useState(EMPTY_BEHAVIOR)
  const [speakerDraft, setSpeakerDraft] =
    useState<SpeakerKey>(DEFAULT_SPEAKER_KEY)
  const [speakingPaceDraft, setSpeakingPaceDraft] = useState<SpeakingPace>(
    DEFAULT_SPEAKING_PACE
  )
  const [backgroundAmbienceDraft, setBackgroundAmbienceDraft] =
    useState<BackgroundAmbience>(DEFAULT_BACKGROUND_AMBIENCE)
  const preserveDraftOnHydration = useRef(false)
  const preserveGreetingDraftOnHydration = useRef(false)
  const [textGreeting, setTextGreeting] = useState('')
  const [voiceGreetingDraft, setVoiceGreetingDraft] = useState('')
  const [voiceGreetingRetryPending, setVoiceGreetingRetryPending] =
    useState(false)
  const [insuranceDraft, setInsuranceDraft] =
    useState<InsuranceDraft>(EMPTY_INSURANCE)
  // The saved insurance the draft was built from; edits survive refetches.
  const insuranceBase = useRef<string | null>(null)
  const insuranceDraftRef = useRef(insuranceDraft)
  insuranceDraftRef.current = insuranceDraft
  const [behaviorSourceTenant, setBehaviorSourceTenant] = useState<
    string | null
  >(null)
  const [voiceOutcome, setVoiceOutcome] = useState<Outcome>(null)
  const [insuranceNotice, setInsuranceNotice] = useState<string | null>(null)

  useEffect(() => {
    // Local form state must never outlive its tenant provenance. React Query
    // scopes the server cache, while this reset scopes unsaved editor state.
    setBehavior(EMPTY_BEHAVIOR)
    setSpeakerDraft(DEFAULT_SPEAKER_KEY)
    setSpeakingPaceDraft(DEFAULT_SPEAKING_PACE)
    setBackgroundAmbienceDraft(DEFAULT_BACKGROUND_AMBIENCE)
    setVoiceGreetingDraft('')
    setVoiceGreetingRetryPending(false)
    setInsuranceDraft(EMPTY_INSURANCE)
    insuranceBase.current = null
    setBehaviorSourceTenant(null)
    setVoiceOutcome(null)
    setInsuranceNotice(null)
  }, [tenantId])

  useEffect(() => {
    if (preserveDraftOnHydration.current) {
      preserveDraftOnHydration.current = false
      if (behaviorQuery.data?.configured) {
        // The saved behavior is always current; only the Voice drafts follow
        // the server here (a failed draft stays selected).
        setBehavior(behaviorQuery.data.behavior)
        setSpeakerDraft(
          behaviorQuery.data.behavior.voiceProfile?.speakerKey ??
            DEFAULT_SPEAKER_KEY
        )
        setSpeakingPaceDraft(
          behaviorQuery.data.behavior.voiceProfile?.speakingPace ??
            DEFAULT_SPEAKING_PACE
        )
        setBackgroundAmbienceDraft(
          behaviorQuery.data.behavior.voiceProfile?.backgroundAmbience ??
            DEFAULT_BACKGROUND_AMBIENCE
        )
      }
      return
    }
    if (!behaviorQuery.data?.configured) {
      const initial = behaviorQuery.data?.initialBehavior ?? EMPTY_BEHAVIOR
      setBehavior(initial)
      setVoiceGreetingDraft(initial.firstMessage)
      setBehaviorSourceTenant(tenantId)
      setSpeakerDraft(initial.voiceProfile?.speakerKey ?? DEFAULT_SPEAKER_KEY)
      setSpeakingPaceDraft(
        initial.voiceProfile?.speakingPace ?? DEFAULT_SPEAKING_PACE
      )
      setBackgroundAmbienceDraft(
        initial.voiceProfile?.backgroundAmbience ?? DEFAULT_BACKGROUND_AMBIENCE
      )
      return
    }
    setBehavior(behaviorQuery.data.behavior)
    if (!preserveGreetingDraftOnHydration.current) {
      setVoiceGreetingDraft(behaviorQuery.data.behavior.firstMessage)
    }
    preserveGreetingDraftOnHydration.current = false
    setBehaviorSourceTenant(tenantId)
    setSpeakerDraft(
      behaviorQuery.data.behavior.voiceProfile?.speakerKey ??
        DEFAULT_SPEAKER_KEY
    )
    setSpeakingPaceDraft(
      behaviorQuery.data.behavior.voiceProfile?.speakingPace ??
        DEFAULT_SPEAKING_PACE
    )
    setBackgroundAmbienceDraft(
      behaviorQuery.data.behavior.voiceProfile?.backgroundAmbience ??
        DEFAULT_BACKGROUND_AMBIENCE
    )
  }, [behaviorQuery.data, tenantId])

  // The saved (or, before configuration, initial) behavior every save builds on.
  const canonical = behaviorQuery.data
    ? behaviorQuery.data.configured
      ? behaviorQuery.data.behavior
      : behaviorQuery.data.initialBehavior
    : undefined
  const insuranceKey = useMemo(
    () => (canonical ? JSON.stringify(insuranceFromBehavior(canonical)) : null),
    [canonical]
  )
  useEffect(() => {
    if (insuranceKey === null || insuranceBase.current === insuranceKey) return
    const clean =
      insuranceBase.current === null ||
      JSON.stringify(insuranceDraftRef.current) === insuranceBase.current
    insuranceBase.current = insuranceKey
    if (clean) setInsuranceDraft(JSON.parse(insuranceKey) as InsuranceDraft)
  }, [insuranceKey, tenantId])

  useEffect(() => {
    if (textGreetingQuery.data) {
      setTextGreeting(textGreetingQuery.data.template)
    }
  }, [textGreetingQuery.data])

  const saveInsurance = useMutation({
    mutationFn: (saveTenantId: string) => {
      if (behaviorSourceTenant !== saveTenantId || tenantId !== saveTenantId) {
        throw new Error('Assistant settings tenant provenance is unresolved')
      }
      const data = behaviorQuery.data
      const saved = data?.configured ? data.behavior : data?.initialBehavior
      if (!data || !saved) {
        throw new Error('Assistant behavior is not configured')
      }
      // Built on the latest saved behavior (never an older local copy), so a
      // Voice change saved earlier in this visit is kept.
      return repository.updateBehavior(saveTenantId, behaviorSourceTenant, {
        expectedRevision: data.configured ? data.revision : 0,
        behavior: insurancePayload(
          saved,
          insuranceDraft,
          PLATFORM_ASSISTANT_NAME
        ),
      })
    },
    onMutate: () => setInsuranceNotice(null),
    onSuccess: (data, saveTenantId) => {
      queryClient.setQueryData(
        ['settings', saveTenantId, 'assistant-behavior'],
        data
      )
      if (tenantId === saveTenantId) notify('success', 'Insurance saved')
    },
    onError: async (error, saveTenantId) => {
      const attempted = JSON.stringify(insuranceDraftRef.current)
      await queryClient.invalidateQueries({
        queryKey: ['settings', saveTenantId, 'assistant-behavior'],
      })
      if (tenantId !== saveTenantId) return
      const fresh =
        queryClient.getQueryData<typeof behaviorQuery.data>(behaviorKey)
      const freshBehavior = fresh?.configured
        ? fresh.behavior
        : fresh?.initialBehavior
      const landed =
        committedBeforeVoiceSync(error) &&
        freshBehavior !== undefined &&
        JSON.stringify(insuranceFromBehavior(freshBehavior)) === attempted
      if (landed) {
        setInsuranceNotice(
          'Saved. The Voice assistant didn’t confirm the update yet.'
        )
        notify('success', 'Insurance saved')
        return
      }
      notify(
        'error',
        outdated(error)
          ? 'The Dashboard was updated. Refresh before saving.'
          : 'Insurance couldn’t be saved'
      )
    },
  })
  const saveVoice = useMutation({
    mutationFn: async (saveTenantId: string) => {
      if (behaviorSourceTenant !== saveTenantId || tenantId !== saveTenantId) {
        throw new Error('Assistant settings tenant provenance is unresolved')
      }
      const latest = await repository.getBehavior(saveTenantId)
      const latestBehavior = latest.configured
        ? latest.behavior
        : latest.initialBehavior
      if (!latestBehavior) {
        throw new Error('Assistant behavior is not ready for configuration')
      }
      return repository.updateBehavior(
        saveTenantId,
        saveTenantId,
        {
          expectedRevision: latest.configured ? latest.revision : 0,
          behavior: {
            ...latestBehavior,
            ...(speakerDraft === DEFAULT_SPEAKER_KEY &&
            speakingPaceDraft === DEFAULT_SPEAKING_PACE &&
            backgroundAmbienceDraft === DEFAULT_BACKGROUND_AMBIENCE &&
            !latestBehavior.voiceProfile
              ? {}
              : {
                  voiceProfile: {
                    speakerKey: speakerDraft,
                    ...(speakingPaceDraft === DEFAULT_SPEAKING_PACE &&
                    !latestBehavior.voiceProfile?.speakingPace
                      ? {}
                      : { speakingPace: speakingPaceDraft }),
                    ...(backgroundAmbienceDraft ===
                      DEFAULT_BACKGROUND_AMBIENCE &&
                    !latestBehavior.voiceProfile?.backgroundAmbience
                      ? {}
                      : { backgroundAmbience: backgroundAmbienceDraft }),
                  },
                }),
          },
        },
        latest.configured ? { scope: 'VOICE' } : undefined
      )
    },
    onSuccess: (data, saveTenantId) => {
      if (tenantId === saveTenantId) {
        preserveDraftOnHydration.current = true
      }
      queryClient.setQueryData(
        ['settings', saveTenantId, 'assistant-behavior'],
        data
      )
      if (tenantId === saveTenantId) {
        notify('success', 'Voice settings updated')
      }
    },
    onError: (error, saveTenantId) => {
      if (tenantId === saveTenantId) {
        preserveDraftOnHydration.current = true
        notify(
          'error',
          outdated(error)
            ? 'The Dashboard was updated. Refresh before saving.'
            : 'Voice settings couldn’t be synchronized'
        )
      }
      return queryClient.invalidateQueries({
        queryKey: ['settings', saveTenantId, 'assistant-behavior'],
      })
    },
  })
  const saveVoiceGreeting = useMutation({
    mutationFn: async (saveTenantId: string) => {
      if (behaviorSourceTenant !== saveTenantId || tenantId !== saveTenantId) {
        throw new Error('Assistant settings tenant provenance is unresolved')
      }
      const latest = await repository.getBehavior(saveTenantId)
      if (
        !latest.configured ||
        latest.reconciliationStatus === 'NOT_MATERIALIZED'
      ) {
        const latestBehavior = latest.configured
          ? latest.behavior
          : latest.initialBehavior
        if (!latestBehavior) {
          throw new Error('Assistant behavior is not ready for configuration')
        }
        return repository.updateBehavior(saveTenantId, saveTenantId, {
          expectedRevision: latest.configured ? latest.revision : 0,
          behavior: { ...latestBehavior, firstMessage: voiceGreetingDraft },
        })
      }
      return repository.updateVoiceGreeting(saveTenantId, saveTenantId, {
        expectedRevision: latest.revision,
        firstMessage: voiceGreetingDraft,
      })
    },
    onSuccess: (data, saveTenantId) => {
      queryClient.setQueryData(
        ['settings', saveTenantId, 'assistant-behavior'],
        data
      )
      if (tenantId === saveTenantId) {
        setVoiceGreetingRetryPending(false)
        setVoiceGreetingDraft(
          data.configured ? data.behavior.firstMessage : voiceGreetingDraft
        )
        notify('success', 'Voice greeting saved')
      }
    },
    onError: (error, saveTenantId) => {
      if (tenantId === saveTenantId) {
        preserveGreetingDraftOnHydration.current = true
        setVoiceGreetingRetryPending(true)
        notify(
          'error',
          outdated(error)
            ? 'The Dashboard was updated. Refresh before saving.'
            : 'Voice greeting couldn’t be synchronized'
        )
      }
      return queryClient.invalidateQueries({
        queryKey: ['settings', saveTenantId, 'assistant-behavior'],
      })
    },
  })
  const saveTextGreeting = useMutation({
    mutationFn: () =>
      repository.updateTextGreeting(tenantId, {
        expectedRevision: textGreetingQuery.data?.revision ?? 0,
        template: textGreeting,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', tenantId, 'text-greeting'], data)
      notify('success', 'Text greeting saved')
    },
    onError: () => {
      notify('error', 'Text greeting couldn’t be saved')
      return queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'text-greeting'],
      })
    },
  })

  const behaviorDraftAvailable = behaviorQuery.data
    ? behaviorQuery.data.configured ||
      Boolean(behaviorQuery.data.initialBehavior)
    : false
  const provenanceReady = behaviorSourceTenant === tenantId
  const voiceReconciliationStatus = behaviorQuery.data?.configured
    ? (behaviorQuery.data.reconciliationStatus ?? 'IN_SYNC')
    : 'NOT_APPLICABLE'
  const voiceSynchronized = voiceReconciliationStatus === 'IN_SYNC'
  const synced = behaviorQuery.data?.configured && voiceSynchronized
  const syncedProfile = synced
    ? behaviorQuery.data?.configured
      ? behaviorQuery.data.behavior.voiceProfile
      : undefined
    : undefined
  const currentSpeakerKey: SpeakerKey | null = synced
    ? (syncedProfile?.speakerKey ?? DEFAULT_SPEAKER_KEY)
    : null
  const currentSpeakingPace: SpeakingPace | null = synced
    ? (syncedProfile?.speakingPace ?? DEFAULT_SPEAKING_PACE)
    : null
  const currentBackgroundAmbience: BackgroundAmbience | null = synced
    ? (syncedProfile?.backgroundAmbience ?? DEFAULT_BACKGROUND_AMBIENCE)
    : null
  const storedSpeaker = behavior.voiceProfile?.speakerKey ?? DEFAULT_SPEAKER_KEY
  const voiceDirty =
    behaviorDraftAvailable &&
    (speakerDraft !== storedSpeaker ||
      speakingPaceDraft !==
        (behavior.voiceProfile?.speakingPace ?? DEFAULT_SPEAKING_PACE) ||
      backgroundAmbienceDraft !==
        (behavior.voiceProfile?.backgroundAmbience ??
          DEFAULT_BACKGROUND_AMBIENCE))
  const greetingDirty =
    behaviorDraftAvailable &&
    (voiceGreetingRetryPending ||
      voiceGreetingDraft.trim() !== behavior.firstMessage)
  const phoneSafeGreeting = isPhoneSafeGreeting(voiceGreetingDraft.trim())
  const voicePanelDirty = voiceDirty || greetingDirty
  const voiceSaving = saveVoice.isPending || saveVoiceGreeting.isPending
  const voiceProblem =
    greetingDirty && !voiceGreetingDraft.trim()
      ? 'enter a greeting to save'
      : greetingDirty && !phoneSafeGreeting
        ? 'replace the text-line greeting to save'
        : null
  const speakers =
    storedSpeaker === 'eric' || speakerDraft === 'eric'
      ? [...CURATED_SPEAKERS, RETIRED_SPEAKER]
      : CURATED_SPEAKERS

  const textSaved = textGreetingQuery.data?.template
  const textDirty = textSaved !== undefined && textGreeting !== textSaved
  const missingTokens = TEXT_TOKENS.filter(
    (token) => !textGreeting.includes(token)
  )
  const unknownToken = /\{(?!assistantName\}|clinicName\})[^{}]+\}/.test(
    textGreeting
  )
  const textProblem = !textGreeting.trim()
    ? 'enter a greeting to save'
    : missingTokens.length
      ? `include ${missingTokens.join(' and ')} to save`
      : unknownToken
        ? 'use only {assistantName} and {clinicName} to save'
        : null

  const insuranceDirty =
    insuranceKey !== null && JSON.stringify(insuranceDraft) !== insuranceKey
  const insuranceIssue = insuranceProblem(insuranceDraft)

  useReportUnsavedChanges('assistant:voice', voicePanelDirty)
  useReportUnsavedChanges('assistant:text', textDirty)
  useReportUnsavedChanges('assistant:insurance', insuranceDirty)

  async function saveVoicePanel() {
    setVoiceOutcome(null)
    let step: 'voice' | 'greeting' = 'voice'
    try {
      if (voiceDirty) await saveVoice.mutateAsync(tenantId)
      step = 'greeting'
      if (greetingDirty) await saveVoiceGreeting.mutateAsync(tenantId)
      setVoiceOutcome({ kind: 'saved' })
    } catch (error) {
      setVoiceOutcome({
        kind: 'error',
        message: outdated(error)
          ? 'The Dashboard was updated. Refresh before saving.'
          : step === 'voice'
            ? 'Voice settings weren’t applied. Your changes are still here; try again.'
            : 'The greeting wasn’t saved. Your changes are still here; try again.',
      })
    }
  }

  function discardVoicePanel() {
    setSpeakerDraft(storedSpeaker)
    setSpeakingPaceDraft(
      behavior.voiceProfile?.speakingPace ?? DEFAULT_SPEAKING_PACE
    )
    setBackgroundAmbienceDraft(
      behavior.voiceProfile?.backgroundAmbience ?? DEFAULT_BACKGROUND_AMBIENCE
    )
    setVoiceGreetingDraft(behavior.firstMessage)
    setVoiceGreetingRetryPending(false)
    setVoiceOutcome(null)
  }

  const textPreview = textGreeting
    .replaceAll('{assistantName}', PLATFORM_ASSISTANT_NAME)
    .replaceAll('{clinicName}', tenantLabel)

  return (
    <section className={panel.section} aria-label="AI and rules">
      <CallHandlingSettings tenantId={tenantId} repository={repository} />
      {behaviorQuery.isPending || rulesQuery.isPending ? (
        <Spinner label="Loading AI settings" />
      ) : behaviorQuery.isError || rulesQuery.isError ? (
        <StatePanel
          kind="error"
          title="AI settings unavailable"
          description="Assistant Behavior and Special Rules could not be loaded safely."
        />
      ) : (
        <>
          <section
            className={panel.panel}
            aria-labelledby="voice-assistant-title"
          >
            <header className={panel.panelHeader}>
              <div className={panel.heading}>
                <span className={panel.eyebrow}>Voice</span>
                <h2 id="voice-assistant-title">Voice Assistant Behavior</h2>
                <p>How {PLATFORM_ASSISTANT_NAME} sounds and greets callers.</p>
              </div>
            </header>
            <div className={styles.voiceBody}>
              {behaviorQuery.data?.configured &&
              voiceReconciliationStatus === 'STALE' &&
              !voiceDirty ? (
                <div className={styles.syncNotice} role="status">
                  <span>
                    Saved selection pending Voice synchronization. The live
                    assistant hasn’t picked up the last voice change yet.
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={voiceSaving || !provenanceReady}
                    onClick={() => {
                      setVoiceOutcome(null)
                      saveVoice.mutate(tenantId)
                    }}
                  >
                    <RefreshCw size={14} aria-hidden="true" />{' '}
                    {saveVoice.isPending ? 'Applying…' : 'Retry'}
                  </Button>
                </div>
              ) : null}
              {voiceReconciliationStatus === 'NOT_MATERIALIZED' ? (
                <p className={styles.quietNote}>
                  <Info size={15} aria-hidden="true" />
                  Voice isn’t live for this clinic yet. Saved settings are used
                  when it goes live.
                </p>
              ) : null}
              {!behaviorQuery.data?.configured && !behaviorDraftAvailable ? (
                <p className={styles.quietNote}>
                  <Info size={15} aria-hidden="true" />
                  Not configured. No values have been inferred or migrated.
                </p>
              ) : null}

              <div className={panel.field}>
                <label
                  htmlFor={`voice-greeting-${tenantId}`}
                  className={panel.fieldLabel}
                >
                  Voice greeting
                </label>
                <textarea
                  id={`voice-greeting-${tenantId}`}
                  className={styles.greetingInput}
                  value={voiceGreetingDraft}
                  maxLength={GREETING_MAX}
                  rows={2}
                  disabled={!behaviorDraftAvailable || voiceSaving}
                  aria-invalid={!phoneSafeGreeting}
                  aria-describedby={`voice-greeting-hint-${tenantId}`}
                  onChange={(event) => {
                    setVoiceGreetingRetryPending(false)
                    setVoiceOutcome(null)
                    setVoiceGreetingDraft(event.currentTarget.value)
                  }}
                />
                <small
                  id={`voice-greeting-hint-${tenantId}`}
                  className={styles.fieldFoot}
                >
                  <span>
                    What callers hear first. Text messages have their own
                    greeting.
                  </span>
                  <span>
                    {voiceGreetingDraft.length}/{GREETING_MAX}
                  </span>
                </small>
                {!phoneSafeGreeting ? (
                  <small className={panel.fieldError} role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    This greeting describes a text channel. Replace it with an
                    inbound phone-call greeting before saving.
                  </small>
                ) : null}
              </div>

              <VoiceChoiceGroup
                legend="Speaker"
                name={`voice-speaker-${tenantId}`}
                options={speakers}
                value={speakerDraft}
                current={currentSpeakerKey}
                disabled={!behaviorDraftAvailable || voiceSaving}
                variant="cards"
                testId="speaker-options"
                onChange={(key) => {
                  setVoiceOutcome(null)
                  setSpeakerDraft(key)
                }}
              />
              <div className={styles.voicePair}>
                <VoiceChoiceGroup
                  legend="Speaking pace"
                  name={`voice-speaking-pace-${tenantId}`}
                  options={CURATED_SPEAKING_PACES}
                  value={speakingPaceDraft}
                  current={currentSpeakingPace}
                  disabled={!behaviorDraftAvailable || voiceSaving}
                  variant="segmented"
                  testId="speaking-pace-options"
                  onChange={(key) => {
                    setVoiceOutcome(null)
                    setSpeakingPaceDraft(key)
                  }}
                />
                <VoiceChoiceGroup
                  legend="Background ambience"
                  name={`voice-background-ambience-${tenantId}`}
                  options={CURATED_BACKGROUND_AMBIENCES}
                  value={backgroundAmbienceDraft}
                  current={currentBackgroundAmbience}
                  disabled={!behaviorDraftAvailable || voiceSaving}
                  variant="segmented"
                  testId="background-ambience-options"
                  onChange={(key) => {
                    setVoiceOutcome(null)
                    setBackgroundAmbienceDraft(key)
                  }}
                />
              </div>
            </div>
            <SettingsSaveFooter
              dirty={voicePanelDirty}
              saving={voiceSaving}
              saved={voiceOutcome?.kind === 'saved' && !voicePanelDirty}
              error={
                voiceOutcome?.kind === 'error' && voicePanelDirty
                  ? voiceOutcome.message
                  : null
              }
              problem={voiceProblem}
              canSave={
                provenanceReady && behaviorDraftAvailable && !voiceProblem
              }
              saveLabel="Save voice settings"
              onDiscard={discardVoicePanel}
              onSave={() => void saveVoicePanel()}
            />
          </section>

          <RulesPanel
            key={`voice-rules-${tenantId}`}
            tenantId={tenantId}
            repository={repository}
            data={rulesQuery.data}
            scope="VOICE_ONLY"
          />

          <section
            className={panel.panel}
            aria-labelledby="text-greeting-title"
          >
            <header className={panel.panelHeader}>
              <div className={panel.heading}>
                <span className={panel.eyebrow}>Text</span>
                <h2 id="text-greeting-title">Text / SMS Assistant Behavior</h2>
                <p>
                  The opening message for a new text conversation, saved
                  separately from Voice and Insurance.
                </p>
              </div>
            </header>
            <div className={styles.voiceBody}>
              <div className={panel.field}>
                <label
                  htmlFor={`text-greeting-${tenantId}`}
                  className={panel.fieldLabel}
                >
                  Text/SMS greeting
                </label>
                <textarea
                  id={`text-greeting-${tenantId}`}
                  className={styles.greetingInput}
                  value={textGreeting}
                  maxLength={GREETING_MAX}
                  rows={2}
                  disabled={saveTextGreeting.isPending}
                  aria-describedby={`text-greeting-hint-${tenantId}`}
                  onChange={(event) =>
                    setTextGreeting(event.currentTarget.value)
                  }
                />
                <small
                  id={`text-greeting-hint-${tenantId}`}
                  className={styles.fieldFoot}
                >
                  <span>
                    Use {'{assistantName}'} and {'{clinicName}'}; both fill in
                    from clinic Settings.
                  </span>
                  <span>
                    {textGreeting.length}/{GREETING_MAX}
                  </span>
                </small>
              </div>
              {textGreeting.trim() ? (
                <p className={styles.textPreview}>
                  <span>Preview</span>
                  {textPreview}
                </p>
              ) : null}
            </div>
            <SettingsSaveFooter
              dirty={textDirty}
              saving={saveTextGreeting.isPending}
              saved={saveTextGreeting.isSuccess && !textDirty}
              problem={textDirty ? textProblem : null}
              canSave={!textProblem}
              saveLabel="Save Text Greeting"
              onDiscard={() => setTextGreeting(textSaved ?? '')}
              onSave={() => saveTextGreeting.mutate()}
            />
          </section>

          <section className={panel.panel} aria-labelledby="insurance-title">
            <header className={panel.panelHeader}>
              <div className={panel.heading}>
                <span className={panel.eyebrow}>Calls and texts</span>
                <h2 id="insurance-title">Insurance Handling</h2>
                <p>
                  What {PLATFORM_ASSISTANT_NAME} asks and tells patients about
                  insurance.
                </p>
              </div>
            </header>
            <div className={styles.insuranceBody}>
              {insuranceNotice ? (
                <div className={styles.syncNotice} role="status">
                  <span>{insuranceNotice}</span>
                </div>
              ) : null}
              <div className={styles.switchRow}>
                <div>
                  <label htmlFor={`proactive-insurance-${tenantId}`}>
                    Ask patients about insurance proactively
                  </label>
                  <p id={`proactive-insurance-description-${tenantId}`}>
                    While booking by phone or text, {PLATFORM_ASSISTANT_NAME}{' '}
                    asks which dental insurance the patient has. Insurance never
                    blocks a booking, and she still answers insurance questions
                    when this is off.
                  </p>
                </div>
                <Switch
                  id={`proactive-insurance-${tenantId}`}
                  aria-describedby={`proactive-insurance-description-${tenantId}`}
                  checked={insuranceDraft.proactive}
                  disabled={
                    saveInsurance.isPending ||
                    !provenanceReady ||
                    !behaviorDraftAvailable
                  }
                  onChange={(event) => {
                    const proactive = event.currentTarget.checked
                    setInsuranceNotice(null)
                    setInsuranceDraft((current) => ({ ...current, proactive }))
                  }}
                />
              </div>
              <InsuranceLists
                carriers={insuranceDraft.carriers}
                notes={insuranceDraft.notes}
                disabled={!behaviorDraftAvailable || saveInsurance.isPending}
                onCarriersChange={(carriers) => {
                  setInsuranceNotice(null)
                  setInsuranceDraft((current) => ({ ...current, carriers }))
                }}
                onNotesChange={(notes) => {
                  setInsuranceNotice(null)
                  setInsuranceDraft((current) => ({ ...current, notes }))
                }}
              />
            </div>
            <SettingsSaveFooter
              dirty={insuranceDirty}
              saving={saveInsurance.isPending}
              saved={saveInsurance.isSuccess && !insuranceDirty}
              error={
                saveInsurance.isError && insuranceDirty
                  ? 'Insurance wasn’t saved. Your changes are still here; try again.'
                  : null
              }
              problem={insuranceIssue}
              canSave={
                provenanceReady && behaviorDraftAvailable && !insuranceIssue
              }
              saveLabel="Save insurance"
              onDiscard={() => {
                if (insuranceKey)
                  setInsuranceDraft(JSON.parse(insuranceKey) as InsuranceDraft)
                setInsuranceNotice(null)
              }}
              onSave={() => saveInsurance.mutate(tenantId)}
            />
          </section>

          <RulesPanel
            key={`general-rules-${tenantId}`}
            tenantId={tenantId}
            repository={repository}
            data={rulesQuery.data}
            scope="GENERAL"
          />
        </>
      )}
    </section>
  )
}
